import type { Geometry } from 'geojson'
import { featuresToGoogleBoundsLiteral } from './mapBounds'
import type { AppMapPadding } from './googleMapTypes'

export const DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS = 750

export type GoogleCameraSnapshot = {
  heading: number
  lat: number
  lng: number
  tilt: number
  zoom: number
}

export type GoogleCameraTarget = {
  center?: google.maps.LatLngLiteral
  heading?: number
  padding?: AppMapPadding
  tilt?: number
  zoom?: number
}

export type GoogleMapHomeView = {
  heading?: number
  latitude: number
  longitude: number
  tilt?: number
  zoom: number
}

export type GoogleMapPadding = number | google.maps.Padding

type NormalizedGoogleMapPadding = {
  bottom: number
  left: number
  right: number
  top: number
}

export function getGoogleMapCamera(
  map: google.maps.Map
): GoogleCameraSnapshot | null {
  const center = map.getCenter()

  if (!center) return null

  return {
    heading: map.getHeading() ?? 0,
    lat: center.lat(),
    lng: center.lng(),
    tilt: map.getTilt() ?? 0,
    zoom: map.getZoom() ?? 0,
  }
}

export function moveGoogleMapCamera(
  map: google.maps.Map,
  target: GoogleCameraTarget
) {
  map.moveCamera(getPaddedGoogleMapCameraTarget(map, target))
}

function normalizeGoogleMapPadding(
  padding: AppMapPadding | undefined
): NormalizedGoogleMapPadding | null {
  if (typeof padding === 'undefined') return null

  if (typeof padding === 'number') {
    return {
      bottom: padding,
      left: padding,
      right: padding,
      top: padding,
    }
  }

  return {
    bottom: padding.bottom ?? 0,
    left: padding.left ?? 0,
    right: padding.right ?? 0,
    top: padding.top ?? 0,
  }
}

export function getPaddedGoogleMapCameraTarget(
  map: google.maps.Map,
  target: GoogleCameraTarget
): GoogleCameraTarget {
  const cameraTarget = { ...target }
  delete cameraTarget.padding

  return {
    ...cameraTarget,
    center: getPaddedGoogleMapCenter(map, target),
  }
}

function getPaddedGoogleMapCenter(
  map: google.maps.Map,
  target: GoogleCameraTarget
) {
  if (!target.center) return undefined

  const padding = normalizeGoogleMapPadding(target.padding)
  if (!padding) return target.center

  const projection = map.getProjection()
  const zoom = target.zoom ?? map.getZoom()

  if (!projection || typeof zoom !== 'number') return target.center

  const scale = 256 * 2 ** zoom
  const targetPoint = projection.fromLatLngToPoint(target.center)

  if (!targetPoint) return target.center

  const offsetX = (padding.left - padding.right) / 2 / scale
  const offsetY = (padding.top - padding.bottom) / 2 / scale

  if (!Number.isFinite(offsetX) || !Number.isFinite(offsetY)) {
    return target.center
  }

  const paddedPoint = new google.maps.Point(
    targetPoint.x - offsetX,
    targetPoint.y - offsetY
  )
  const paddedCenter = projection.fromPointToLatLng(paddedPoint)

  if (!paddedCenter) return target.center

  return {
    lat: paddedCenter.lat(),
    lng: paddedCenter.lng(),
  }
}

export function getPanToCameraTarget(
  center: google.maps.LatLngLiteral
): GoogleCameraTarget {
  return { center }
}

export function getZoomByCameraTarget(
  map: google.maps.Map,
  delta: number
): GoogleCameraTarget {
  return {
    zoom: (map.getZoom() ?? 0) + delta,
  }
}

export function getHomeCameraTarget(
  homeView: GoogleMapHomeView
): GoogleCameraTarget {
  return {
    center: {
      lat: homeView.latitude,
      lng: homeView.longitude,
    },
    heading: homeView.heading,
    tilt: homeView.tilt,
    zoom: homeView.zoom,
  }
}

export function getClusterCameraTarget(
  center: google.maps.LatLngLiteral,
  zoom: number
): GoogleCameraTarget {
  return {
    center,
    zoom,
  }
}

export function fitGoogleMapBounds(
  map: google.maps.Map,
  bounds: google.maps.LatLngBounds | google.maps.LatLngBoundsLiteral,
  padding?: GoogleMapPadding
) {
  map.fitBounds(bounds, padding)
}

export function fitGoogleMapFeatures(
  map: google.maps.Map,
  features: Array<{ geometry: Geometry }>,
  padding?: GoogleMapPadding
) {
  const bounds = featuresToGoogleBoundsLiteral(features)

  if (!bounds) return false

  fitGoogleMapBounds(map, bounds, padding)

  return true
}
